import type { ReactNode } from "react";
import { useAuth } from "../lib/auth";
import { SyncRoot } from "../sync/SyncRoot";
import { Onboarding } from "./Onboarding";
import { Login } from "./Login";
import { ResetPassword } from "./ResetPassword";
import { AuthCallback, readAuthError } from "./AuthCallback";

// The email-confirmation redirect lands on /confirm (shared with the mobile route
// for one cross-platform universal link); /auth/confirm is kept for any older
// links still in inboxes. Auth-redirect errors can arrive on any path.
function isAuthCallback(): boolean {
  const p = window.location.pathname;
  return p === "/confirm" || p === "/auth/confirm" || readAuthError() !== null;
}

// The password-reset link redirects here. The PKCE code exchange (detectSessionInUrl)
// makes it look like a normal sign-in, so the path — not an auth event — is what
// signals recovery. ResetPassword manages its own verifying/session states.
function isPasswordRecovery(): boolean {
  return window.location.pathname === "/reset-password";
}

// Gates the app behind auth: nothing while the session resolves, the Login form
// when signed out, the set-new-password screen during a reset-link recovery, and
// the app + sync drivers when signed in. (Sign-out lives in the sidebar's account
// row — see AppLayout.)
export function AuthGate({ children }: { children: ReactNode }) {
  const { session, loading, recovering } = useAuth();

  if (isAuthCallback()) return <AuthCallback />;
  if (isPasswordRecovery()) return <ResetPassword />;
  if (loading) return null;
  if (recovering) return <ResetPassword />;
  if (!session) return <Login />;

  return (
    <>
      <SyncRoot />
      {children}
      {/* First-run intro overlay. Self-gates on the shared, account-synced
          hasSeenOnboarding flag (once prefs are pulled), so it only shows once. */}
      <Onboarding />
    </>
  );
}
